import { useQueryClient, type Query } from '@tanstack/react-query'
import { useEffect, useState, useSyncExternalStore } from 'react'

import {
  QueryActivityTracker,
  systemClock,
  type QueryActivityClock,
  type QueryActivitySnapshot
} from '../lib/query-activity.ts'

/**
 * Whether a query counts as a datalake query.
 *
 * A query opts out with `meta: QUIET_QUERY_META`. One that polls (a
 * `refetchInterval`) is left out without it, because a heartbeat must never
 * keep the indicator up.
 */
function counts(query: Query): boolean {
  return (
    query.meta?.quiet !== true &&
    !query.observers.some(observer => observer.options.refetchInterval)
  )
}

function report(tracker: QueryActivityTracker, query: Query): void {
  const { fetchStatus, status } = query.state
  if (!counts(query)) {
    // A query that turned quiet, or began to poll, mid-fetch ends here, so it
    // cannot hold the batch open. The tracker ignores an id it never saw.
    tracker.set(query.queryHash, 'ok')

    return
  }
  if (fetchStatus === 'idle') {
    tracker.set(query.queryHash, status === 'error' ? 'error' : 'ok')

    return
  }
  // 'paused' waits for the network and has not answered yet, so it still runs.
  tracker.set(query.queryHash, 'running')
}

/**
 * Watch every react-query fetch in the app and report them as one batch.
 *
 * The cache is the one place every data request passes through, retries
 * included, so no page carries code for this. Queries only: a mutation writes
 * the employee's own settings or documents and is not a datalake query.
 *
 * @param clock - The time source. Tests pass a clock they move by hand.
 * @returns The batch the indicator draws.
 */
export function useQueryActivity(
  clock: QueryActivityClock = systemClock
): QueryActivitySnapshot {
  const client = useQueryClient()
  const [tracker] = useState(() => new QueryActivityTracker(clock))

  useEffect(() => {
    const cache = client.getQueryCache()
    for (const query of cache.getAll()) report(tracker, query)
    const unsubscribe = cache.subscribe(event => {
      if (event.type === 'removed') {
        tracker.set(event.query.queryHash, 'ok')

        return
      }
      report(tracker, event.query)
    })

    return () => {
      unsubscribe()
      tracker.reset()
    }
  }, [client, tracker])

  return useSyncExternalStore(tracker.subscribe, tracker.getSnapshot)
}
